import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertOctagon, AlertTriangle, Clock, PlayCircle, StopCircle, Archive, Trash2, Terminal, Server, List, Info, Camera, PlusCircle, Search, Copy, Check } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';

const DaytonaIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleSandbox, setExampleSandbox] = useState('');
  const [exampleDeveloper, setExampleDeveloper] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [apiKey, setApiKey] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [credentialTab, setCredentialTab] = useState<'dashboard' | 'cli'>('dashboard');

  const webhookUrl = `${window.location.origin}/api/webhooks/daytona`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getDaytonaStatus();
      if (data?.connected) {
        setConnected(true);
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectDaytona({ api_key: apiKey, api_url: apiUrl || undefined, webhook_secret: webhookSecret });
      setSuccessMsg('Daytona connected successfully!');
      setApiKey('');
      setWebhookSecret('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectDaytona();
      setConnected(false);
      setSuccessMsg('Daytona disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testDaytona();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Daytona (${data.sandbox_count ?? 0} sandbox${data.sandbox_count !== 1 ? 'es' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}

      {connected ? (
        <DaytonaConnectedView
          navigate={navigate}
          testing={testing}
          testResult={testResult}
          lastWebhookAt={lastWebhookAt}
          webhookEventCount={webhookEventCount}
          webhookUrl={webhookUrl}
          exampleSandbox={exampleSandbox}
          setExampleSandbox={setExampleSandbox}
          exampleDeveloper={exampleDeveloper}
          setExampleDeveloper={setExampleDeveloper}
          onTest={handleTest}
          onDisconnect={handleDisconnect}
        />
      ) : (
        <DaytonaConnectForm
          apiKey={apiKey}
          setApiKey={setApiKey}
          apiUrl={apiUrl}
          setApiUrl={setApiUrl}
          webhookSecret={webhookSecret}
          setWebhookSecret={setWebhookSecret}
          credentialTab={credentialTab}
          setCredentialTab={setCredentialTab}
          saving={saving}
          onConnect={handleConnect}
          webhookUrl={webhookUrl}
        />
      )}
    </div>
  );
};

export default DaytonaIntegration;

interface ConnectedViewProps {
  navigate: ReturnType<typeof useNavigate>;
  testing: boolean;
  testResult: { success: boolean; message: string } | null;
  lastWebhookAt: string | null;
  webhookEventCount: number;
  webhookUrl: string;
  exampleSandbox: string;
  setExampleSandbox: (v: string) => void;
  exampleDeveloper: string;
  setExampleDeveloper: (v: string) => void;
  onTest: () => void;
  onDisconnect: () => void;
}

function DaytonaConnectedView(props: ConnectedViewProps) {
  const {
    navigate, testing, testResult, lastWebhookAt, webhookEventCount, webhookUrl,
    exampleSandbox, setExampleSandbox, exampleDeveloper, setExampleDeveloper,
    onTest, onDisconnect,
  } = props;

  const triggers = [
    { name: 'Sandbox Created', desc: 'Triggers when a new Daytona sandbox is created.', Icon: PlusCircle },
    { name: 'Sandbox Stopped', desc: 'Triggers when a sandbox transitions out of the started state.', Icon: StopCircle },
    { name: 'Sandbox Error', desc: 'Triggers when a sandbox enters an error or build-failed state.', Icon: AlertOctagon },
    { name: 'Sandbox Archived', desc: 'Triggers when a sandbox is archived.', Icon: Archive },
    { name: 'Snapshot Build Failed', desc: 'Triggers when a snapshot enters an error/build-failed state.', Icon: AlertTriangle },
    { name: 'Volume Error', desc: 'Triggers when a volume enters an error state.', Icon: AlertTriangle },
  ];
  const actions = [
    { name: 'List Sandboxes', desc: 'List the sandboxes in the organization.', Icon: List },
    { name: 'Create Sandbox', desc: 'Create a sandbox from a snapshot or Docker image.', Icon: PlusCircle },
    { name: 'Get Sandbox', desc: 'Retrieve a sandbox\u2019s state and metadata.', Icon: Info },
    { name: 'Start / Stop Sandbox', desc: 'Start or stop a sandbox.', Icon: PlayCircle },
    { name: 'Archive / Delete Sandbox', desc: 'Archive or permanently delete a sandbox.', Icon: Trash2 },
    { name: 'Run Command', desc: 'Execute a shell command inside a sandbox.', Icon: Terminal },
    { name: 'Set Auto-Stop', desc: 'Set a sandbox\u2019s inactivity auto-stop interval.', Icon: Clock },
    { name: 'List / Create / Delete Snapshot', desc: 'Manage reusable sandbox snapshots.', Icon: Camera },
    { name: 'List / Get / Create / Delete Volume', desc: 'Manage persistent volumes.', Icon: Server },
    { name: 'Investigate Daytona', desc: 'AI investigates failing sandboxes, snapshots, and volumes.', Icon: Search },
  ];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <DaytonaIcon className="w-7 h-7 text-foreground" />
            <div>
              <CardTitle className="flex items-center gap-2">
                Daytona
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
              </CardTitle>
              <CardDescription>Sandbox, snapshot, and volume lifecycle, command execution, and investigation automation</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Webhook Events Received</span>
              <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Webhook</span>
              <p className="font-medium">{lastWebhookAt ? new Date(lastWebhookAt).toLocaleString() : 'No events received yet'}</p>
            </div>
          </div>

          {testResult && (
            <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
              {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
              <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={onTest} disabled={testing} className="gap-1">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Test Connection
            </Button>
            <Button variant="outline" size="sm" onClick={onDisconnect} className="gap-1">
              <Unplug className="w-3.5 h-3.5" /> Disconnect
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Webhook</CardTitle>
          <CardDescription>
            Daytona delivers sandbox, snapshot, and volume events to Kestrel via the webhook endpoint you registered in the Daytona dashboard. Each delivery is verified with your signing secret before it can fire a workflow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm">Webhook URL</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input readOnly value={webhookUrl} className="font-mono text-xs" />
              <Button variant="outline" size="sm" className="gap-1 shrink-0" onClick={() => navigator.clipboard.writeText(webhookUrl)}>
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              This is the endpoint registered in <strong className="text-foreground">Daytona Dashboard → Webhooks</strong>. To rotate the signing secret, update it in Daytona, then Disconnect and reconnect here with the new secret.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
          <CardDescription>Use these Daytona actions and triggers in your workflows to automate sandbox monitoring and incident response.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 mb-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {triggers.map(block => (
                <div key={block.name} className="border rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {actions.map(block => (
                <div key={block.name} className="border rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Example Workflow</CardTitle>
          <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
            When a Daytona sandbox errors for the{' '}
            <input
              value={exampleSandbox}
              onChange={e => setExampleSandbox(e.target.value)}
              placeholder="project"
              style={{ width: `${Math.max(exampleSandbox.length || 7, 7)}ch` }}
              className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
            />{' '}
            project, investigate the root cause, DM the{' '}
            <input
              value={exampleDeveloper}
              onChange={e => setExampleDeveloper(e.target.value)}
              placeholder="developer"
              style={{ width: `${Math.max(exampleDeveloper.length || 9, 9)}ch` }}
              className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
            />{' '}
            on Slack with the summary, and create a Jira bug ticket.
          </div>
          <Button
            size="sm"
            className="gap-1.5"
            disabled={!exampleSandbox || !exampleDeveloper}
            onClick={() => navigate('/workflows/new', {
              state: {
                suggestion: {
                  nl_prompt: `When a Daytona sandbox errors for the ${exampleSandbox} project, investigate the root cause, DM ${exampleDeveloper} on Slack with the summary, and create a Jira bug ticket.`,
                },
              },
            })}
          >
            Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

interface ConnectFormProps {
  apiKey: string;
  setApiKey: (v: string) => void;
  apiUrl: string;
  setApiUrl: (v: string) => void;
  webhookSecret: string;
  setWebhookSecret: (v: string) => void;
  credentialTab: 'dashboard' | 'cli';
  setCredentialTab: (v: 'dashboard' | 'cli') => void;
  saving: boolean;
  onConnect: () => void;
  webhookUrl: string;
}

function DaytonaConnectForm(props: ConnectFormProps) {
  const { apiKey, setApiKey, apiUrl, setApiUrl, webhookSecret, setWebhookSecret, credentialTab, setCredentialTab, saving, onConnect, webhookUrl } = props;
  const cliCommands = `brew install daytonaio/cli/daytona
daytona login
daytona api-key create kestrel`;
  const [cliCopied, setCliCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const copyCli = () => {
    navigator.clipboard.writeText(cliCommands);
    setCliCopied(true);
    setTimeout(() => setCliCopied(false), 2000);
  };
  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <DaytonaIcon className="w-7 h-7 text-foreground" />
          <div>
            <CardTitle>Daytona</CardTitle>
            <CardDescription>Connect Daytona for sandbox, snapshot, and volume lifecycle, command execution, and investigation automation in workflows.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm">API Key</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Kestrel authenticates to Daytona with an organization <strong className="text-foreground">API key</strong>, sent as a Bearer token to <strong className="text-foreground">app.daytona.io</strong> to read sandbox, snapshot, and volume state and perform actions. Create one from the Daytona dashboard or the CLI.
          </p>
          <div className="inline-flex rounded-md border border-input bg-background p-0.5 mb-3">
            <button
              type="button"
              onClick={() => setCredentialTab('dashboard')}
              className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                credentialTab === 'dashboard' ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Daytona Dashboard
            </button>
            <button
              type="button"
              onClick={() => setCredentialTab('cli')}
              className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                credentialTab === 'cli' ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              CLI
            </button>
          </div>

          {credentialTab === 'dashboard' ? (
            <div className="rounded-md border border-input bg-muted/40 p-4 mb-3">
              <ol className="space-y-3 text-sm text-muted-foreground">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                  <span>In the <strong className="text-foreground">Daytona dashboard</strong>, open <strong className="text-foreground">API Keys</strong> in the left sidebar, then click <strong className="text-foreground">Create API Key</strong> (top right).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                  <span>Enter a <strong className="text-foreground">Key Name</strong> (e.g. <code className="bg-background px-1 rounded">kestrel</code>).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                  <span>Optionally set an <strong className="text-foreground">Expires</strong> date. Leave it blank for a non-expiring key, or pick a date and set a calendar reminder to rotate the key in Kestrel before it expires.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                  <span>
                    For <strong className="text-foreground">Permissions</strong>, choose <strong className="text-foreground">Full Access</strong> (simplest — Kestrel manages sandboxes, snapshots, and volumes).
                    <br />
                    To scope it down instead, choose <strong className="text-foreground">Restricted</strong> and grant <strong className="text-foreground">Read + Write + Delete</strong> on <strong className="text-foreground">Sandboxes</strong> and <strong className="text-foreground">Snapshots</strong>, plus <strong className="text-foreground">Read</strong> on <strong className="text-foreground">Volumes</strong>. The <strong className="text-foreground">Sandboxes</strong> preset alone is not enough — snapshot and volume actions will fail without those grants.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">5</span>
                  <span>Click <strong className="text-foreground">Create</strong>, copy the generated <strong className="text-foreground">API key</strong> (shown once), and paste it below.</span>
                </li>
              </ol>
            </div>
          ) : (
            <div className="rounded-md border border-input bg-muted/40 p-3 mb-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                Install the Daytona CLI and log in, then create an API key. The CLI prompts for the same options as the dashboard — a name, an optional expiration, and permissions (choose <strong className="text-foreground">Full Access</strong>, or grant Read/Write/Delete on Sandboxes &amp; Snapshots and Read on Volumes):
              </p>
              <div className="relative">
                <pre className="text-xs font-mono bg-background border border-input rounded px-3 py-2 pr-10 overflow-x-auto whitespace-pre">{cliCommands}</pre>
                <button
                  type="button"
                  onClick={copyCli}
                  aria-label="Copy commands"
                  className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  {cliCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Copy the generated key (shown once) and paste it below.
              </p>
            </div>
          )}

          <Input
            id="daytona-key"
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Your Daytona API key"
            className="mt-1 font-mono"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Stored encrypted and only used to authenticate requests to the Daytona API.
          </p>
        </div>
        <div>
          <Label htmlFor="daytona-url" className="text-sm">API URL <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input
            id="daytona-url"
            value={apiUrl}
            onChange={e => setApiUrl(e.target.value)}
            placeholder="https://app.daytona.io/api"
            className="mt-1 font-mono"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Only needed for self-hosted Daytona. Leave blank to use Daytona Cloud.
          </p>
        </div>
        <div>
          <Label className="text-sm">Webhook Setup <span className="text-red-500">*</span></Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Kestrel receives Daytona sandbox, snapshot, and volume events through a webhook. Set it up in the Daytona dashboard, then paste the signing secret below.
          </p>
          <div className="rounded-md border border-input bg-muted/40 p-4 mb-3">
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>In the <strong className="text-foreground">Daytona dashboard</strong>, open <strong className="text-foreground">Webhooks</strong>. If webhooks aren't enabled yet, click <strong className="text-foreground">Enable webhooks</strong>, then click <strong className="text-foreground">Create Endpoint</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <div className="min-w-0 flex-1">
                  <span>Give it a name and set the <strong className="text-foreground">Endpoint URL</strong> to:</span>
                  <div className="relative mt-1.5">
                    <pre className="text-xs font-mono bg-background border border-input rounded px-3 py-2 pr-10 overflow-x-auto whitespace-pre">{webhookUrl}</pre>
                    <button
                      type="button"
                      onClick={copyUrl}
                      aria-label="Copy webhook URL"
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                      {urlCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>For <strong className="text-foreground">Events</strong>, select all sandbox, snapshot, and volume events, then click <strong className="text-foreground">Create</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                <span>Click the newly created endpoint to open its details, reveal its <strong className="text-foreground">Signing Secret</strong>, and copy it.</span>
              </li>
            </ol>
          </div>
          <Input
            id="daytona-webhook-secret"
            type="password"
            value={webhookSecret}
            onChange={e => setWebhookSecret(e.target.value)}
            placeholder="Daytona webhook signing secret (whsec_...)"
            className="mt-1 font-mono"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Stored encrypted. Kestrel verifies every webhook delivery against this secret before firing a workflow.
          </p>
        </div>
        <Button onClick={onConnect} disabled={saving || !apiKey || !webhookSecret}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Connect Daytona
        </Button>
      </CardContent>
    </Card>
  );
}

